import { MixTemplate, InstrumentPosition } from '@/types/production.types';

export const MIX_TEMPLATES: MixTemplate[] = [
  // ═══════════════════════════════════════════════════════════════
  // SOUTHERN ROCK / CLASSIC ROCK
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'southern-rock-dual-guitar',
    name: 'Southern Rock (Dual Guitar Attack)',
    description: 'Classic Skynyrd-style dual guitar configuration with Hammond B3 filling the middle.',
    genre: ['southern rock', 'classic rock', 'blues rock'],
    productionSignature: {
      ampCharacter: 'High-headroom tube breakup pushed by playing dynamics rather than gain-stage distortion; two guitars harmonize in complementary registers instead of doubling the same part.',
      gainStaging: 'amp-driven',
      captureApproach: 'Close-mic\'d combo/half-stack cabs tracked mostly live in the room, letting the two guitars bleed slightly for a natural sense of space.',
      dynamicApproach: 'Volume-knob and pick-attack driven dynamics over compression; loud sections earn their loudness through performance, not limiting.',
      eraTexture: 'Warm early-70s analog tracking character — present low-mids, minimal high-end brittleness.'
    },
    positions: [
      { instrumentId: 'drums-rock', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-rhythm', pan: -70, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-lead', pan: 70, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'hammond-b3', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'slide-guitar', pan: 55, depth: 'front', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'piano-honkytonk', pan: 35, depth: 'back', level: 'quiet', frequencySlot: 'high-mid' }
    ]
  },
  {
    id: 'southern-rock-single-guitar',
    name: 'Southern Rock (Single Guitar)',
    description: 'Focused power-trio style southern rock with organ support.',
    genre: ['southern rock', 'blues rock'],
    productionSignature: {
      ampCharacter: 'Single cranked tube amp carrying the full rhythm/lead role, edge-of-breakup rather than saturated.',
      gainStaging: 'amp-driven',
      captureApproach: 'Close-mic\'d single cab, tighter and drier than the dual-guitar variant since there is no second guitar to fill space.',
      dynamicApproach: 'Wide dynamic range between verse restraint and chorus push, carried by playing rather than mix automation.',
      eraTexture: 'Lean, room-forward analog warmth with organ occupying the frequency space a second guitar would otherwise fill.'
    },
    positions: [
      { instrumentId: 'drums-rock', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-rhythm', pan: -40, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'hammond-b3', pan: 40, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'harmonica', pan: 0, depth: 'front', level: 'medium', frequencySlot: 'high-mid' }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // COUNTRY / AMERICANA
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'outlaw-country-full',
    name: 'Outlaw Country (Full Band)',
    description: 'The Waylon/Willie setup: heavy phaser on guitar, prominent pedal steel, and dry drums.',
    genre: ['outlaw country', 'country rock'],
    productionSignature: {
      ampCharacter: 'Clean-to-edge Telecaster-style twang, phaser/chorus coloration rather than gain-stage distortion.',
      gainStaging: 'pedal-driven',
      captureApproach: 'Dry, close-mic\'d drums with minimal room ambience; vocal and pedal steel kept upfront and unprocessed-sounding.',
      dynamicApproach: 'Deliberately uncompressed, performance-driven dynamics — a reaction against polished Nashville production of the same era.',
      eraTexture: 'Dry 70s outlaw-country austerity — intentionally rougher and less orchestrated than mainstream country of the same period.'
    },
    positions: [
      { instrumentId: 'drums-country', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'acoustic-guitar-strummed', pan: -20, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-twangy', pan: 45, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'pedal-steel', pan: -50, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'fiddle', pan: 30, depth: 'back', level: 'quiet', frequencySlot: 'high' }
    ]
  },
  {
    id: 'outlaw-stripped',
    name: 'Outlaw Country (Stripped)',
    description: 'Intimate acoustic focus with minimal electric coloration.',
    genre: ['outlaw country', 'americana', 'singer-songwriter'],
    productionSignature: {
      ampCharacter: 'Minimal electric presence; acoustic guitar carries the harmonic weight clean and unprocessed.',
      gainStaging: 'clean-direct',
      captureApproach: 'Close-mic\'d acoustic guitar and voice as the focal point, drums pushed to the back or omitted entirely.',
      dynamicApproach: 'Wide-open, unmixed-sounding dynamics — intimacy comes from restraint, not loudness.',
      eraTexture: 'Bare-bones singer-songwriter honesty; sounds closer to a demo than a studio production by design.'
    },
    positions: [
      { instrumentId: 'acoustic-guitar-fingerpicked', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'harmonica', pan: 30, depth: 'mid', level: 'quiet', frequencySlot: 'high-mid' },
      { instrumentId: 'drums-country', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'americana-roots',
    name: 'Americana/Roots',
    description: 'Natural, organic acoustic textures with wide stereo strings.',
    genre: ['americana', 'folk', 'bluegrass'],
    productionSignature: {
      ampCharacter: 'Entirely acoustic-instrument driven — no amp saturation, tonal color comes from the instruments themselves.',
      gainStaging: 'clean-direct',
      captureApproach: 'Room-mic\'d ensemble capturing natural interplay between fiddle, mandolin, and guitars rather than close-mic\'d isolation.',
      dynamicApproach: 'Natural acoustic dynamic range preserved, light-touch compression at most.',
      eraTexture: 'Organic, unprocessed roots-music warmth — de-emphasizes studio polish in favor of ensemble feel.'
    },
    positions: [
      { instrumentId: 'acoustic-guitar-strummed', pan: -30, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'acoustic-guitar-fingerpicked', pan: 30, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'mandolin', pan: 55, depth: 'front', level: 'medium', frequencySlot: 'high' },
      { instrumentId: 'fiddle', pan: -55, depth: 'front', level: 'medium', frequencySlot: 'high' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'low' }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // METAL / HEAVY
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'doom-wall-of-sound',
    name: 'Doom (Wall of Sound)',
    description: 'Massive, slow-moving heavy-weight mix with center-weighted low end.',
    genre: ['doom metal', 'stoner rock'],
    productionSignature: {
      ampCharacter: 'Heavily downtuned, high-gain amp stacks driven hard and sustained rather than palm-muted — tone built for sustain over articulation.',
      gainStaging: 'amp-driven',
      captureApproach: 'Layered/doubled guitar tracking to build mass, low end centered rather than spread for maximum weight.',
      dynamicApproach: 'Deliberately narrow dynamic range — the "wall" effect comes from sustained saturation, not dynamic contrast.',
      eraTexture: 'Thick, sludgy low-mid density inherited from 70s heavy-blues amplification pushed far past its clean ceiling.'
    },
    positions: [
      { instrumentId: 'drums-doom', pan: 0, depth: 'back', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-doom', pan: -40, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-doom', pan: 40, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'synthesizer-pad', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'sludge-swamp',
    name: 'Sludge Metal (Swamp)',
    description: 'Murky, humid mix with aggressive mid-range saturation and thick bass.',
    genre: ['sludge metal', 'southern doom'],
    productionSignature: {
      ampCharacter: 'Amp gain stacked with fuzz for a murkier, less defined saturation than straight amp-driven doom — texture over clarity.',
      gainStaging: 'hybrid',
      captureApproach: 'Front-of-cab close mic\'ing pushed hot into saturation; less concerned with fidelity than with density.',
      dynamicApproach: 'Compressed into a dense, humid mass rather than left open — the "swamp" quality is a deliberately congested mix.',
      eraTexture: 'Southern-doom mid-range grime — dirtier and less hi-fi than northern doom metal\'s cleaner low end.'
    },
    positions: [
      { instrumentId: 'drums-doom', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-heavy', pan: -30, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-heavy', pan: 30, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'hammond-b3-dirty', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'groove-metal-tight',
    name: 'Groove Metal (Tight)',
    description: 'Precision rhythmic lock between kick and guitar chugs.',
    genre: ['groove metal', 'thrash'],
    productionSignature: {
      ampCharacter: 'Modern high-gain amp tone scooped in the mids for palm-mute clarity, tight low end rather than sustained wash.',
      gainStaging: 'amp-driven',
      captureApproach: 'Tightly gated, close-mic\'d tracking with heavy editing/quantization emphasis to lock guitar chugs to the kick drum.',
      dynamicApproach: 'Aggressively gated dynamics — silence between hits is as intentional as the hits themselves, for rhythmic punch.',
      eraTexture: '90s-2000s precision-metal tightness — closer to a drum machine\'s rhythmic certainty than a loose jam feel.'
    },
    positions: [
      { instrumentId: 'drums-metal', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy-pick', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-djent', pan: -80, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-djent', pan: 80, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-lead-shred', pan: 0, depth: 'front', level: 'medium', frequencySlot: 'high-mid' }
    ]
  },
  {
    id: 'stoner-fuzz',
    name: 'Stoner Rock (Fuzz)',
    description: 'Warm, fuzzy, mid-forward desert rock production.',
    genre: ['stoner rock', 'desert rock'],
    productionSignature: {
      ampCharacter: 'Vintage-voiced fuzz pedal saturation into a clean-ish amp, rather than amp-stage overdrive — softer, rounder clipping.',
      gainStaging: 'pedal-driven',
      captureApproach: 'Loose, room-friendly tracking; less concerned with tight edits than with a hazy, jammed-out feel.',
      dynamicApproach: 'Loose, unquantized dynamics that breathe — grooves settle into a pocket rather than snapping to a grid.',
      eraTexture: 'Warm vintage fuzz-pedal texture with a dry desert-rock dustiness rather than polished modern high-gain clarity.'
    },
    positions: [
      { instrumentId: 'drums-doom', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-heavy', pan: -50, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'electric-guitar-heavy', pan: 50, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // GLAM / ARENA
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'glam-metal-arena',
    name: 'Glam Metal (Arena)',
    description: 'Large-scale 80s production with gated snare and wide-stereo chorused guitars.',
    genre: ['glam metal', 'arena rock'],
    productionSignature: {
      ampCharacter: 'Amp-driven gain layered with chorus/flange for a wide, shimmering guitar wall rather than a raw single-source tone.',
      gainStaging: 'hybrid',
      captureApproach: 'Heavily doubled/quadrupled guitar tracking panned hard for maximum stereo width; drums close-mic\'d with aggressive gating.',
      dynamicApproach: 'Highly compressed and gated — the snare\'s sharp cutoff and consistent guitar level are deliberate 80s production signatures.',
      eraTexture: 'Bright, gated-reverb 80s arena sheen — polished and larger-than-life rather than raw or live-sounding.'
    },
    positions: [
      { instrumentId: 'drums-arena', pan: 0, depth: 'back', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-bright', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low-mid' },
      { instrumentId: 'electric-guitar-80s', pan: -90, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-80s', pan: 90, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-lead-shred', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'high-mid' },
      { instrumentId: 'synthesizer-pad-80s', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'air' }
    ]
  },
  {
    id: 'glam-ballad',
    name: 'Glam Ballad',
    description: 'Intimate piano start building to explosive power-ballad finale.',
    genre: ['glam metal', 'ballad'],
    productionSignature: {
      ampCharacter: 'Clean-to-mild-breakup guitar tone during verses, opening into fuller amp-driven gain only at the climax.',
      gainStaging: 'hybrid',
      captureApproach: 'Intimate close-mic\'d piano and vocal opening, expanding to full-band tracking as the arrangement builds.',
      dynamicApproach: 'Deliberately staged dynamic build across the song\'s length — quiet-to-loud arc is the entire production strategy.',
      eraTexture: 'Lush 80s power-ballad reverb tails and wide strings, contrasted against an intimate opening.'
    },
    positions: [
      { instrumentId: 'piano-ballad', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'drums-ballad', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-80s', pan: -60, depth: 'back', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'strings-section', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'high' }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // BLUES / FUNK / GOSPEL
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'blues-trio',
    name: 'Blues (Trio)',
    description: 'Raw, dynamic power-trio mix. Guitar, bass, and drums in the room.',
    genre: ['blues', 'blues rock'],
    productionSignature: {
      ampCharacter: 'Natural tube amp breakup driven entirely by pick attack and volume, no pedal coloration between guitar and amp.',
      gainStaging: 'amp-driven',
      captureApproach: 'Minimal-mic, room-forward tracking — the trio is captured playing together, not layered separately.',
      dynamicApproach: 'Wide, unrestrained dynamic range; loud and soft passages are a direct read of the performance.',
      eraTexture: 'Raw, live-in-the-room honesty with minimal studio polish — imperfection is part of the character.'
    },
    positions: [
      { instrumentId: 'drums-shuffle', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-electric-blues', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-blues', pan: -30, depth: 'front', level: 'loud', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'blues-full-band',
    name: 'Blues (Full Band)',
    description: 'Trio plus piano, organ, and horn section punctuation.',
    genre: ['blues', 'soul'],
    productionSignature: {
      ampCharacter: 'Amp-driven guitar breakup as the trio, with organ and horns adding warm harmonic support rather than competing gain sources.',
      gainStaging: 'amp-driven',
      captureApproach: 'Ensemble tracking with horns and keys treated as call-and-response punctuation, mixed to answer the vocal/guitar lines.',
      dynamicApproach: 'Dynamic peaks reserved for horn stabs and vocal ad-libs — the rhythm section holds a steady pocket underneath.',
      eraTexture: 'Warm analog studio-band tracking character, closer to a live revue than a heavily overdubbed record.'
    },
    positions: [
      { instrumentId: 'drums-shuffle', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-electric-blues', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-blues', pan: -40, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'hammond-b3', pan: 40, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'horns-section', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'high-mid' },
      { instrumentId: 'piano-honkytonk', pan: -60, depth: 'back', level: 'quiet', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'gospel-full',
    name: 'Gospel (Full)',
    description: 'Lush, powerful gospel mix featuring massive choir stacks and organ.',
    genre: ['gospel', 'soul'],
    productionSignature: {
      ampCharacter: 'Organ and voice carry the harmonic weight; minimal guitar distortion, warmth comes from the Hammond/Leslie tone instead.',
      gainStaging: 'clean-direct',
      captureApproach: 'Wide stereo choir capture aiming for a room-filling ensemble sound rather than isolated close-mic\'d voices.',
      dynamicApproach: 'Big, unrestrained dynamic swells from intimate verses to full-choir peaks — dynamics are the emotional engine of the mix.',
      eraTexture: 'Warm, reverberant church-hall ambience baked into the tracking, not just added afterward.'
    },
    positions: [
      { instrumentId: 'drums-gospel', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-gospel', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'hammond-b3-gospel', pan: -45, depth: 'mid', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'piano-gospel', pan: 45, depth: 'mid', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'choir-subtle', pan: 0, depth: 'back', level: 'loud', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'funk-full',
    name: 'Funk (Full)',
    description: 'Percussive and syncopated. Slap bass and scratchy guitars leading the way.',
    genre: ['funk', 'groove'],
    productionSignature: {
      ampCharacter: 'Clean-to-edge guitar tone valued for percussive attack, not sustain — the "chicken scratch" rhythm technique needs a tight, dry amp response.',
      gainStaging: 'clean-direct',
      captureApproach: 'Tight, dry, transient-forward drum and bass capture — the pocket has to read clearly with minimal smearing.',
      dynamicApproach: 'Fast, punchy dynamics driven by rhythmic syncopation rather than sustained loudness; groove-forward, not power-forward.',
      eraTexture: 'Dry, tight 70s funk-studio precision — reverb and ambience are minimized so the rhythm section stays crisp.'
    },
    positions: [
      { instrumentId: 'drums-funk', pan: 0, depth: 'front', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'bass-slap', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'electric-guitar-funk', pan: -50, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'clavinet', pan: 50, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'horns-section', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'punk-raw',
    name: 'Punk (Raw)',
    description: 'Fast, aggressive, and garage-oriented. Minimal separation.',
    genre: ['punk', 'hardcore'],
    productionSignature: {
      ampCharacter: 'Amp-driven raw overdrive with minimal shaping — the tone is meant to sound unrefined and immediate, not sculpted.',
      gainStaging: 'amp-driven',
      captureApproach: 'Deliberately minimal mic\'ing and separation, closer to a live-room garage recording than a controlled studio session.',
      dynamicApproach: 'Consistently loud and flat by design — punk production intentionally rejects wide dynamic range as too polished.',
      eraTexture: 'Lo-fi, unpolished garage-tape character — roughness is the point, not a flaw to fix.'
    },
    positions: [
      { instrumentId: 'drums-punk', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-punk', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-punk', pan: -20, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-punk', pan: 20, depth: 'front', level: 'loud', frequencySlot: 'mid' }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // HIGH-FIDELITY NEW COUNTRY PROFILES (Preserved)
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'modern-country-full',
    name: 'Modern Country (2020s Radio)',
    description: 'Contemporary 2020s radio-ready country. Polished with organic textures and balanced blend.',
    genre: ['modern country', 'contemporary country'],
    productionSignature: {
      ampCharacter: 'Clean-to-edge electric guitar blended with acoustic layers, tone shaped for radio brightness over amp character.',
      gainStaging: 'hybrid',
      captureApproach: 'Hybrid tracking — live instruments layered with sample-augmented drums and heavy vocal comping/tuning, typical of modern radio production.',
      dynamicApproach: 'Heavily compressed and loudness-mastered, minimal dynamic range by design to compete on radio/streaming platforms.',
      eraTexture: '2020s pop-adjacent country polish — organic instrumentation with a fully modern, digitally-refined sheen.'
    },
    positions: [
      { instrumentId: 'drums-country', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'acoustic-guitar-strummed', pan: -15, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-twangy', pan: 40, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'pedal-steel', pan: 50, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'piano-ballad', pan: -30, depth: 'back', level: 'quiet', frequencySlot: 'mid' },
      { instrumentId: 'fiddle', pan: -50, depth: 'back', level: 'quiet', frequencySlot: 'high' }
    ]
  },
  {
    id: 'bro-country-full',
    name: 'Bro-Country (Stadium)',
    description: 'Big loud rock-influenced country with wall of guitars and driving rhythm.',
    genre: ['bro country', 'country rock'],
    productionSignature: {
      ampCharacter: 'Rock-derived amp-driven gain borrowed wholesale from arena rock, layered with country instrumentation on top rather than blended into it.',
      gainStaging: 'amp-driven',
      captureApproach: 'Big, front-loaded close-mic\'d tracking prioritizing loudness and impact over subtlety.',
      dynamicApproach: 'Heavily compressed, consistently loud — built for stadium and truck-stereo playback, not dynamic nuance.',
      eraTexture: '2010s stadium-country maximalism — country instrumentation pushed through a hard-rock production lens.'
    },
    positions: [
      { instrumentId: 'drums-rock', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy-pick', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-heavy', pan: -80, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-heavy', pan: 80, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'acoustic-guitar-strummed', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'banjo', pan: 30, depth: 'mid', level: 'medium', frequencySlot: 'high' }
    ]
  },
  {
    id: 'nashville-lush',
    name: 'Nashville Country (Lush Strings)',
    description: 'Classic Nashville Sound with string section, piano, and refined studio craft.',
    genre: ['nashville sound', 'smooth country'],
    productionSignature: {
      ampCharacter: 'Smooth, clean electric and acoustic guitar tone, deliberately restrained to let strings and piano carry the harmonic richness.',
      gainStaging: 'clean-direct',
      captureApproach: 'Session-orchestra tracking approach — string section recorded/arranged with classical-adjacent care, not treated as an afterthought overdub.',
      dynamicApproach: 'Moderate, controlled dynamics with tasteful compression — refinement over rawness is the entire aesthetic.',
      eraTexture: 'Classic 1960s "Nashville Sound" pop-country polish — countrypolitan smoothness aimed at crossover radio appeal.'
    },
    positions: [
      { instrumentId: 'drums-ballad', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'mid' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'piano-ballad', pan: -20, depth: 'front', level: 'loud', frequencySlot: 'mid' },
      { instrumentId: 'acoustic-guitar-fingerpicked', pan: 25, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'strings-section', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'pedal-steel', pan: 40, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' }
    ]
  },
  {
    id: 'jimmy-jam-terry-lewis-r-and-b',
    name: 'Jimmy Jam-Terry Lewis R&B',
    description: 'A polished, groove-based mix template reminiscent of the 1980s Minneapolis sound, blending funk, synth-pop, and R&B elements with a touch of electronic flair.',
    genre: ['R&B', 'Funk', 'Synth-Pop', 'Electronic'],
    productionSignature: {
      ampCharacter: 'Synth and drum-machine driven — electric guitar plays a minor textural role rather than carrying the arrangement.',
      gainStaging: 'synth-direct',
      captureApproach: 'Programmed/sequenced tracking with tightly gated drum-machine sounds, layered synth stacks recorded direct rather than mic\'d.',
      dynamicApproach: 'Tight, punchy, quantized dynamics — the groove is precision-engineered rather than performance-driven.',
      eraTexture: 'Glossy 1980s Minneapolis-sound electronic-funk sheen — synth-forward and rhythmically crisp.'
    },
    positions: [
      { instrumentId: 'drums-funk', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-bright', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'synthesizer-pad-80s', pan: -40, depth: 'back', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'synthesizer-pad-80s', pan: 40, depth: 'back', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'electric-guitar-funk', pan: -60, depth: 'mid', level: 'quiet', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-funk', pan: 60, depth: 'mid', level: 'quiet', frequencySlot: 'mid' },
      { instrumentId: 'piano-ballad', pan: 0, depth: 'mid', level: 'subtle', frequencySlot: 'low-mid' }
    ]
  },
  {
    id: 'motown-male-group-ballad',
    name: 'Motown Male Group (Ballad)',
    description: 'Lush, warm mix for a 1960s Motown male vocal group ballad. Focuses on tight harmonies and a rich orchestral backdrop.',
    genre: ['Motown', 'Soul', 'Ballad'],
    productionSignature: {
      ampCharacter: 'Restrained, clean guitar sitting well under vocal harmonies and strings rather than competing for attention.',
      gainStaging: 'clean-direct',
      captureApproach: 'Tightly blended vocal-group stacking with a live studio-orchestra backdrop, mixed for vocal-harmony clarity above all else.',
      dynamicApproach: 'Warm analog tape-style compression gluing the ensemble together, moderate and controlled dynamic range.',
      eraTexture: 'Warm 1960s Motown-studio tape saturation and mono-adjacent center-weighted mix character.'
    },
    positions: [
      { instrumentId: 'drums-ballad', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'piano-ballad', pan: -20, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 20, depth: 'mid', level: 'quiet', frequencySlot: 'high-mid' },
      { instrumentId: 'strings-section', pan: 0, depth: 'back', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'horns-section', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'choir-subtle', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'philadelphia-soul-classic',
    name: 'Philadelphia Soul (Classic)',
    description: 'Classic Philadelphia soul mix featuring lush strings, smooth vocals, and a driving rhythm section. Emphasizes warmth and clarity.',
    genre: ['soul', 'r&b', 'funk'],
    productionSignature: {
      ampCharacter: 'Clean, articulate rhythm guitar in a supporting funk role, tone shaped for warmth rather than edge.',
      gainStaging: 'clean-direct',
      captureApproach: 'Full studio-orchestra tracking approach — strings and horns recorded as a genuine ensemble layered over a tight rhythm section.',
      dynamicApproach: 'Smooth, controlled dynamics with orchestral swells reserved for choruses; rhythm section stays consistently in the pocket.',
      eraTexture: 'Lush early-70s orchestral-soul sophistication — more polished and arranged than Southern soul, warmer than disco.'
    },
    positions: [
      { instrumentId: 'drums-funk', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-funk', pan: -40, depth: 'mid', level: 'quiet', frequencySlot: 'high-mid' },
      { instrumentId: 'electric-guitar-funk', pan: 40, depth: 'mid', level: 'quiet', frequencySlot: 'high-mid' },
      { instrumentId: 'piano-ballad', pan: -20, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'strings-section', pan: 0, depth: 'back', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'horns-section', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'power-ballad-arena-rock-80s',
    name: 'Power Ballad (Arena Rock 80s)',
    description: 'A classic power ballad mix with prominent piano and soaring guitars. Emphasizes wide stereo image and emotional impact.',
    genre: ['rock', 'ballad', 'arena rock'],
    productionSignature: {
      ampCharacter: 'Clean piano-led verses opening into amp-driven, sustain-heavy lead guitar for the emotional payoff sections.',
      gainStaging: 'hybrid',
      captureApproach: 'Wide-stereo doubled guitar tracking for the climax sections, intimate close-mic\'d piano for the opening.',
      dynamicApproach: 'Staged quiet-to-loud dynamic arc across the song is the core production strategy, mirroring the glam-ballad approach at arena scale.',
      eraTexture: 'Big, reverb-drenched 80s arena-rock emotional maximalism.'
    },
    positions: [
      { instrumentId: 'drums-arena', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'piano-ballad', pan: -20, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: -50, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 50, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad-80s', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'acoustic-singer-songwriter-modern-americana',
    name: 'Acoustic Singer-Songwriter (Modern Americana)',
    description: 'An intimate acoustic mix template, focusing on clear vocals and layered acoustic guitars. Designed for a modern \'unplugged\' sound with warmth and clarity.',
    genre: ['acoustic', 'singer-songwriter', 'americana'],
    productionSignature: {
      ampCharacter: 'No electric amp presence — tonal character comes entirely from acoustic instrument choice and mic placement.',
      gainStaging: 'clean-direct',
      captureApproach: 'Modern high-fidelity acoustic close-mic\'ing, cleaner and more detailed than a vintage folk recording despite the "unplugged" feel.',
      dynamicApproach: 'Gentle, controlled dynamics with light modern compression — intimate but not raw or lo-fi.',
      eraTexture: 'Contemporary "unplugged" clarity — warmth without the tape hiss or room noise of an actual vintage acoustic session.'
    },
    positions: [
      { instrumentId: 'acoustic-guitar-strummed', pan: -35, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'acoustic-guitar-strummed', pan: 35, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'drums-ballad', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'full-spectrum' },
      { instrumentId: 'piano-ballad', pan: -20, depth: 'back', level: 'subtle', frequencySlot: 'full-spectrum' },
      { instrumentId: 'cello', pan: 20, depth: 'back', level: 'subtle', frequencySlot: 'low-mid' }
    ]
  },
  {
    id: 'hard-rock-les-paul-stack',
    name: 'Hard Rock (Les Paul Stack)',
    description: 'Classic hard rock mix with a focus on powerful guitars and driving drums. The mix aims for a wide stereo image with clear separation of instruments.',
    genre: ['rock', 'hard rock'],
    productionSignature: {
      ampCharacter: 'Thick, mid-forward humbucker-into-cranked-amp breakup — the archetypal hard-rock tone built for sustain and weight over articulation.',
      gainStaging: 'amp-driven',
      captureApproach: 'Doubled rhythm guitars panned wide for a "stack" effect, close-mic\'d for maximum presence.',
      dynamicApproach: 'Moderate compression keeping the wall of guitars consistently present without flattening the drums\' impact.',
      eraTexture: 'Classic 70s-80s hard-rock mid-range density — thicker and less scooped than modern metal tones.'
    },
    positions: [
      { instrumentId: 'drums-rock', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-rhythm', pan: -50, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 50, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'thrash-metal-aggressive',
    name: 'Thrash Metal (Aggressive)',
    description: 'A high-energy mix emphasizing speed and aggression. The guitars are heavily distorted, and the drums are punchy and tight.',
    genre: ['metal', 'thrash metal'],
    productionSignature: {
      ampCharacter: 'Tight, scooped-mid high-gain amp tone optimized for fast palm-muted riffing clarity at speed, not sustain.',
      gainStaging: 'amp-driven',
      captureApproach: 'Close-mic\'d, heavily edited tracking to keep fast rhythm parts tight and synchronized across doubled guitars.',
      dynamicApproach: 'Fast, aggressive, low-dynamic-range mixing — everything sits hot and present to match the tempo\'s intensity.',
      eraTexture: '80s thrash-metal aggression — rawer and more mid-scooped than the polish of glam metal from the same decade.'
    },
    positions: [
      { instrumentId: 'drums-metal', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-heavy', pan: -45, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-heavy', pan: 45, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead-shred', pan: 0, depth: 'front', level: 'medium', frequencySlot: 'high-mid' }
    ]
  },
  {
    id: 'progressive-rock-standard',
    name: 'Progressive Rock (Standard)',
    description: 'A standard progressive rock mix featuring layered instruments and spacious soundscapes. Focused on clarity and separation between instruments.',
    genre: ['progressive rock'],
    productionSignature: {
      ampCharacter: 'Moderate amp-driven gain that leaves headroom for complex arrangements — tone serves clarity over raw power.',
      gainStaging: 'amp-driven',
      captureApproach: 'Spacious, well-separated tracking prioritizing instrumental clarity over density, since complex arrangements need room to be heard.',
      dynamicApproach: 'Wide dynamic range preserved deliberately, allowing quiet instrumental passages to contrast sharply with full-band sections.',
      eraTexture: 'Spacious, hi-fi soundstage character — production serves compositional complexity rather than raw impact.'
    },
    positions: [
      { instrumentId: 'drums-rock', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-rhythm', pan: -45, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead', pan: 45, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'hammond-b3', pan: 0, depth: 'mid', level: 'quiet', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'new-wave-synth-pop',
    name: 'New Wave/Synth-Pop',
    description: 'A mix emphasizing clean synths and driving rhythms. Clarity and separation are key to this mix.',
    genre: ['new wave', 'synth-pop'],
    productionSignature: {
      ampCharacter: 'Synth-led arrangement with guitar reduced to a chorus-heavy textural role rather than a lead voice.',
      gainStaging: 'synth-direct',
      captureApproach: 'Programmed drum-machine tracking with synths recorded direct, minimal live-room mic\'ing anywhere in the mix.',
      dynamicApproach: 'Tight, punchy, gated dynamics on drums, consistent synth levels throughout — precision over performance variance.',
      eraTexture: 'Bright, digital-era early-80s synth-pop clarity — clean and crisp rather than warm or analog-saturated.'
    },
    positions: [
      { instrumentId: 'drums-punk', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-bright', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'synthesizer-pad', pan: -40, depth: 'back', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad-80s', pan: 40, depth: 'back', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: -60, depth: 'mid', level: 'quiet', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-rhythm', pan: 60, depth: 'mid', level: 'quiet', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'alt-country-indie-folk-acoustic',
    name: 'Alt-Country/Indie Folk (Acoustic)',
    description: 'An acoustic-driven Alt-Country/Indie Folk mix template featuring layered acoustic guitars and subtle instrumentation. Focuses on a warm, intimate sound.',
    genre: ['Alt-Country', 'Indie Folk'],
    productionSignature: {
      ampCharacter: 'No electric-amp centerpiece — layered acoustic guitars and light fiddle/mandolin coloration carry the arrangement.',
      gainStaging: 'clean-direct',
      captureApproach: 'Warm, close-but-natural acoustic mic\'ing, subtle enough to leave room for understated vocal delivery.',
      dynamicApproach: 'Restrained dynamics throughout, deliberately avoiding big loud/soft contrast in favor of steady intimacy.',
      eraTexture: 'Understated indie-folk warmth — closer to bedroom-recording intimacy than a full Nashville studio production.'
    },
    positions: [
      { instrumentId: 'acoustic-guitar-strummed', pan: -30, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'acoustic-guitar-fingerpicked', pan: 30, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'mandolin', pan: 0, depth: 'front', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'fiddle', pan: 50, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'drums-ballad', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'blues-rock-ballad-slow-blues',
    name: 'Blues Rock Ballad (Slow Blues)',
    description: 'A slow blues ballad mix featuring prominent guitars, fretless bass, and harmonica. The mix emphasizes warmth and emotional depth.',
    genre: ['blues', 'rock', 'ballad'],
    productionSignature: {
      ampCharacter: 'Amp-driven natural breakup on lead guitar, dynamically responsive to touch for the slow-blues expressive bends.',
      gainStaging: 'amp-driven',
      captureApproach: 'Close-mic\'d lead guitar and harmonica as expressive focal points, organ and rhythm guitar kept warm and supportive underneath.',
      dynamicApproach: 'Wide dynamic swells built around expressive phrasing — the slower tempo gives space for volume-driven emotional peaks.',
      eraTexture: 'Warm, unhurried blues-rock intimacy with analog-style low-end roundness.'
    },
    positions: [
      { instrumentId: 'electric-guitar-lead', pan: -30, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 30, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-soft', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'low' },
      { instrumentId: 'drums-ballad', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'hammond-b3', pan: -50, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'harmonica-blues', pan: 50, depth: 'front', level: 'medium', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'arena-rock-non-glam',
    name: 'Arena Rock (Non-Glam)',
    description: 'A powerful and anthemic mix for non-glam arena rock. Features big drums, driving guitars, and strong vocals, creating a larger-than-life sound.',
    genre: ['rock', 'arena rock'],
    productionSignature: {
      ampCharacter: 'Amp-driven, mid-forward gain without the chorus/flange sheen of glam metal — a rawer, more straightforward arena tone.',
      gainStaging: 'amp-driven',
      captureApproach: 'Big-room drum tracking and doubled rhythm guitars, close-mic\'d for punch rather than polished for width.',
      dynamicApproach: 'Moderately gated drums with consistent guitar presence — big but not as hyper-compressed as glam-era production.',
      eraTexture: 'Straightforward arena-rock scale without the overtly 80s glam production signatures (gating, chorus, synth pads).'
    },
    positions: [
      { instrumentId: 'drums-arena', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'low' },
      { instrumentId: 'electric-guitar-rhythm', pan: -45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'djent-modern-metal',
    name: 'Djent (Modern Metal)',
    description: 'A mix template for modern djent, focusing on tight, articulate guitars and powerful drums. Aiming for a clear and wide stereo image.',
    genre: ['djent', 'metal', 'progressive metal'],
    productionSignature: {
      ampCharacter: 'Extended-range guitar tone with tight, articulate low-end palm mutes — modern high-gain amp modeling favored for surgical precision over vintage warmth.',
      gainStaging: 'amp-driven',
      captureApproach: 'Heavily quantized/edited tracking, often DI-recorded and re-amped digitally for maximum tightness and reproducibility.',
      dynamicApproach: 'Extremely tight, sample-augmented drum dynamics locked to a grid — rhythmic precision is the genre\'s defining production trait.',
      eraTexture: '2010s digital-metal precision — clinical and quantized rather than performance-driven looseness.'
    },
    positions: [
      { instrumentId: 'drums-metal', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-djent', pan: -45, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'electric-guitar-djent', pan: 45, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'synthesizer-pad', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'mid' }
    ]
  },
  {
    id: 'west-coast-hip-hop-gangsta-rap',
    name: 'West Coast Hip-Hop (Gangsta Rap)',
    description: 'A mix template capturing the essence of West Coast Hip-Hop, emphasizing a laid-back groove with impactful drums and bass. It is designed to highlight raw vocals and politically charged lyrics.',
    genre: ['hip-hop'],
    productionSignature: {
      ampCharacter: 'Synth-lead melodic hooks over sampled/programmed grooves; guitar and keys play a minor textural role, if present at all.',
      gainStaging: 'sample-based',
      captureApproach: 'Programmed drum-machine and sampled-groove tracking with the vocal recorded dry and upfront for maximum lyrical clarity.',
      dynamicApproach: 'Deep, deliberately laid-back pocket — groove feel prioritized over tight quantization, giving the beat a rolling, unhurried swagger.',
      eraTexture: 'Early-90s G-funk-adjacent warmth — deep sub bass and melodic synth leads over a dry, upfront vocal.'
    },
    positions: [
      { instrumentId: 'drums-funk', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'synthesizer-pad', pan: -40, depth: 'back', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'synthesizer-pad', pan: 40, depth: 'back', level: 'medium', frequencySlot: 'mid' },
      { instrumentId: 'electric-guitar-funk', pan: 25, depth: 'mid', level: 'quiet', frequencySlot: 'high-mid' },
      { instrumentId: 'piano-honkytonk', pan: -25, depth: 'mid', level: 'quiet', frequencySlot: 'high-mid' }
    ]
  },
  {
    id: 'sludge-gregorian-dark-metal',
    name: 'Sludge Gregorian Dark Metal',
    description: 'A crushing blend of sludge metal and dark Gregorian chant, featuring heavy synths and dissonant textures. The mix emphasizes a powerful low end and haunting atmosphere.',
    genre: ['sludge metal', 'dark metal', 'gregorian chant', 'avant-garde'],
    productionSignature: {
      ampCharacter: 'Amp-driven heavy gain blended with wide synth/choir pads for an unusually atmospheric take on sludge weight.',
      gainStaging: 'hybrid',
      captureApproach: 'Layered guitar tracking against a reverberant, wide choir-pad bed — the hybrid instrumentation demands unconventional balancing.',
      dynamicApproach: 'Contrasts crushing loud sections against sparse choir-only passages, leaning on that dynamic gap for its haunting effect.',
      eraTexture: 'Atmospheric, reverb-soaked heaviness — closer to cinematic dark ambient than straightforward sludge metal.'
    },
    positions: [
      { instrumentId: 'drums-doom', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-doom', pan: -45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-doom', pan: 45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad-80s', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'choir-subtle', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'full-spectrum' }
    ]
  },
  {
    id: 'southern-rock-groove-boogie-jam',
    name: 'Southern Rock (Groove Boogie Jam)',
    description: 'A mix template for southern rock with a focus on groove, boogie, and jam band elements. Tight guitars, fat drums, and a locked-in bass provide the foundation for extended instrumental passages and a powerful chorus.',
    genre: ['southern rock', 'groove', 'boogie', 'jam band'],
    productionSignature: {
      ampCharacter: 'Amp-driven loose, warm breakup similar to the dual-guitar southern rock template but with more headroom for extended improvisation.',
      gainStaging: 'amp-driven',
      captureApproach: 'Room-forward tracking that preserves ensemble interplay for extended jam sections rather than tightly edited song structure.',
      dynamicApproach: 'Loose, jam-driven dynamics that build and release organically across extended instrumental passages, not pre-mapped to a fixed arrangement.',
      eraTexture: 'Warm, roomy 70s live-band jam-session character — closer to a live album feel than a tightly produced studio single.'
    },
    positions: [
      { instrumentId: 'drums-rock', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-heavy', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'sub' },
      { instrumentId: 'electric-guitar-rhythm', pan: -45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead', pan: 0, depth: 'mid', level: 'medium', frequencySlot: 'high-mid' },
      { instrumentId: 'slide-guitar', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'mid' },
      { instrumentId: 'hammond-b3', pan: 0, depth: 'back', level: 'subtle', frequencySlot: 'low-mid' }
    ]
  },
  {
    id: 'aor-polished-rock',
    name: 'AOR (Polished Rock)',
    description: 'Mix template for Album-Oriented Rock, emphasizing polished production and melodic hooks. Captures the essence of FM radio\'s golden age with big guitars, layered keyboards, and soaring vocals.',
    genre: ['rock', 'aor'],
    productionSignature: {
      ampCharacter: 'Amp-driven gain smoothed and layered with keyboards for a radio-friendly polish rather than raw hard-rock edge.',
      gainStaging: 'hybrid',
      captureApproach: 'Heavily layered guitar and synth tracking aimed at a dense, radio-ready wall of sound with careful frequency-slot separation.',
      dynamicApproach: 'Consistently high, controlled loudness with tasteful compression — built for FM radio playback consistency.',
      eraTexture: 'Glossy late-70s/80s FM-radio production sheen — melodic and accessible over aggressive or raw.'
    },
    positions: [
      { instrumentId: 'drums-arena', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'bass-electric-bright', pan: 0, depth: 'mid', level: 'loud', frequencySlot: 'low-mid' },
      { instrumentId: 'electric-guitar-rhythm', pan: -45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-rhythm', pan: 45, depth: 'mid', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'electric-guitar-lead', pan: 0, depth: 'front', level: 'loud', frequencySlot: 'full-spectrum' },
      { instrumentId: 'synthesizer-pad-80s', pan: 0, depth: 'back', level: 'medium', frequencySlot: 'full-spectrum' },
      { instrumentId: 'acoustic-guitar-strummed', pan: 0, depth: 'back', level: 'quiet', frequencySlot: 'full-spectrum' }
    ]
  }
];

export function getMixTemplateById(id: string, templates?: MixTemplate[]): MixTemplate | undefined {
  const pool = templates || MIX_TEMPLATES;
  return pool.find(t => t.id === id);
}

export function getMixTemplatesForGenres(genres: string[], templates?: MixTemplate[]): MixTemplate[] {
  const pool = templates || MIX_TEMPLATES;
  const normalizedGenres = genres.map(g => g.toLowerCase());
  return pool.filter(template =>
    template.genre.some(g =>
      normalizedGenres.some(ng => g.toLowerCase().includes(ng) || ng.includes(g.toLowerCase()))
    )
  );
}

export function getBestMixTemplate(genres: string[], templates?: MixTemplate[]): MixTemplate | undefined {
  const pool = templates || MIX_TEMPLATES;
  const matches = getMixTemplatesForGenres(genres, pool);
  if (matches.length === 0) return pool[0];

  const scored = matches.map(template => {
    const score = genres.reduce((acc, genre) => {
      return acc + (template.genre.some(g =>
        g.toLowerCase().includes(genre.toLowerCase())
      ) ? 1 : 0);
    }, 0);
    return { template, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0].template;
}
